import React from 'react';
import compose from 'recompose/compose';
import { ApplicationState } from '../../../store';
import { actionCreators as DebtorLogItemsStoreActionCreators } from '../../../store/DebtorLogItemsStore';
import { connect } from 'react-redux';
import { DebtorLogItem, SelectedDebtorClaim } from '../../../api/client';
import { getCaseNumbersFromQueryString, getDebtorIdsFromQueryString } from '../../../components/Utils';
import { getDebtorLogItems } from '../../../store/Selectors';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import DebtorLogItemComponent from '../../../components/debtorLog/presentation/DebtorLogItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const topRowHeight = 333;

interface PropsFromState {
  logItems: DebtorLogItem[];
  isLoading: boolean;
  selectedDebtorClaims: SelectedDebtorClaim[];
  isLoadingDebtorClaims: boolean;
  loadedClaimIds: number[];
  loadedClaimNumbers: string[];
}

interface State {
  height: number;
}

interface PropsFromDispatch {
  clearDebtorLogItems: typeof DebtorLogItemsStoreActionCreators.clearDebtorLogItems;
  getDebtorLogItems: typeof DebtorLogItemsStoreActionCreators.getDebtorLogItems;
  getDebtorLogItemsByClaimIdsAndNumbers: typeof DebtorLogItemsStoreActionCreators.getDebtorLogItemsByClaimIdsAndNumbers;
}

type ComponentProps = State & PropsFromState & PropsFromDispatch & RouteComponentProps;

class DebtorLog extends React.Component<ComponentProps, State> {
  private get isClaimsRoute() {
    return this.props.location.pathname === '/claims';
  }

  constructor(props: any) {
    super(props);
    this.state = {
      height: window.innerHeight
    };
  }

  updateWindowHeight = () => {
    this.setState({ height: window.innerHeight });
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateWindowHeight, false);

    if (this.isClaimsRoute) {
      // Waiting for claims to load before fetching logs
      return;
    }

    const debtorIds = getDebtorIdsFromQueryString(this.props.location);
    this.props.getDebtorLogItems(debtorIds);
  }

  componentDidUpdate(prevProps: ComponentProps) {
    if (!this.isClaimsRoute) return;

    const claimsJustLoaded =
      prevProps.isLoadingDebtorClaims === true &&
      this.props.isLoadingDebtorClaims === false;

    if (claimsJustLoaded && this.props.loadedClaimNumbers.length > 0) {
      const debtorIds = getDebtorIdsFromQueryString(this.props.location);
      this.props.getDebtorLogItemsByClaimIdsAndNumbers(debtorIds[0], this.props.loadedClaimIds, this.props.loadedClaimNumbers);
    }
  }

  componentWillUnmount() {
    this.props.clearDebtorLogItems();
    window.removeEventListener('resize', this.updateWindowHeight, false);
  }

  public render() {
    const isWaitingForClaims = this.isClaimsRoute && this.props.isLoadingDebtorClaims;

    return (
      <React.Fragment>
        {(this.props.isLoading || isWaitingForClaims) ?
          <CircularProgress sx={{ margin: '16px auto', display: 'block' }} /> :
          this.props.logItems.length === 0 &&
          <Typography sx={{ margin: '16px', fontSize: '14px' }}>Debitor har ingen logg</Typography>
        }
        <Box sx={{
          overflowY: 'auto',
          maxHeight: this.state.height - topRowHeight
        }}>
          {this.props.logItems &&
            this.props.logItems.map((item, i) =>
              <DebtorLogItemComponent
                key={i}
                logItem={item}
                selected={this.props.selectedDebtorClaims && this.props.selectedDebtorClaims.length > 0
                  ? item.debtorClaimIds
                    ? item.debtorClaimIds!.filter(id => this.props.selectedDebtorClaims.filter(n => n.externalReferenceId === id).length > 0).length > 0
                    : item.debtorClaimNumbers
                      ? item.debtorClaimNumbers!.filter(o => this.props.selectedDebtorClaims.filter(n => n.number === o).length > 0).length > 0
                      : false
                  : false}
              />)
          }
        </Box>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  logItems: getDebtorLogItems(state),
  selectedDebtorClaims: state.debtorClaims.selectedDebtorClaims,
  isLoading: state.debtorLogItems.isLoading,
  isLoadingDebtorClaims: state.debtorLeapClaims.isLoading,
  loadedClaimNumbers: state.debtorLeapClaims.claims
  .flatMap(c => [c.number, ...(c.childClaims ?? []).map(child => child.number)])
    .filter((n): n is string => !!n),
  loadedClaimIds: state.debtorLeapClaims.claims
    .flatMap(c => [c.externalReferenceId, ...(c.childClaims ?? []).map(child => child.externalReferenceId)])
    .filter((n): n is number => !!n),
});

const mapDispatchToProps: PropsFromDispatch = {
  clearDebtorLogItems: DebtorLogItemsStoreActionCreators.clearDebtorLogItems,
  getDebtorLogItems: DebtorLogItemsStoreActionCreators.getDebtorLogItems,
  getDebtorLogItemsByClaimIdsAndNumbers: DebtorLogItemsStoreActionCreators.getDebtorLogItemsByClaimIdsAndNumbers,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(withRouter(DebtorLog));
